/**
 * OrgProfilePage — Full home-style layout for viewing an organization.
 * Same 3-column layout as home page, with activity feed.
 * Route: /corporate-hub/:orgId
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Loader2, Building2, MapPin, Search, Users,
  Briefcase, Star, Tag, Phone, Mail, Globe, GraduationCap,
  Verified, MessageSquare, ExternalLink,
  ChevronDown, ChevronUp, UserPlus, Target, ShoppingBag,
  Heart, MessageCircle, Send, Share2, Edit3, Image, Bell, Calendar,
} from "lucide-react";
import TopNav from "@/components/TopNav";
import GeoFilterBar from "@/components/GeoFilterBar";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = (() => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8001/api";
  return `https://api.${host}/api`;
})();

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const dummyLogo = (name) => {
  const colors = ["from-violet-500 to-purple-600", "from-blue-500 to-cyan-600", "from-emerald-500 to-teal-600", "from-amber-500 to-orange-600", "from-rose-500 to-pink-600"];
  return { bg: colors[(name?.charCodeAt(0) || 0) % colors.length], letter: (name || "O")[0].toUpperCase() };
};

const dummyAvatar = (name, id) => {
  const gradients = ["from-emerald-500 to-teal-600", "from-blue-500 to-cyan-600", "from-violet-500 to-purple-600", "from-rose-500 to-pink-600", "from-amber-500 to-orange-600"];
  const h = (id || name || "").length;
  return { bg: gradients[h % gradients.length], letter: (name || "?")[0].toUpperCase() };
};

// ── ORG BUSINESS CARD ──
function OrgBusinessCard({ org, onBack, isOwner }) {
  const navigate = useNavigate();
  const logo = dummyLogo(org.name);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (org && !editing) {
      setEditForm({
        name: org.name || "", description: org.description || "", org_type: org.org_type || "",
        country: org.country || "", city: org.city || "", telephone: org.telephone || "",
        email: org.email || "", website: org.website || "", address: org.address || "",
        category: org.category || "",
        services: (org.services || []).join(", "),
        wishes: (org.wishes || []).join(", "),
      });
    }
  }, [org, editing]);

  const saveOrgProfile = async () => {
    setSaving(true);
    try {
      const payload = {
        name: editForm.name, description: editForm.description, org_type: editForm.org_type,
        country: editForm.country, city: editForm.city, telephone: editForm.telephone,
        email: editForm.email, website: editForm.website, address: editForm.address, category: editForm.category,
        services: editForm.services.split(",").map(s => s.trim()).filter(Boolean),
        wishes: editForm.wishes.split(",").map(s => s.trim()).filter(Boolean),
      };
      await axios.put(API + "/v2/orgs/" + (org.id || org.subdomain), payload, { headers: getHeaders() });
      toast.success("Org profile saved!");
      setEditing(false);
      window.location.reload();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  if (editing) {
    return (
      <div className="h-full">
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5">
          <Edit3 className="w-3.5 h-3.5 text-violet-400" /> Edit Org Profile
        </h3>
        <div className="space-y-2 text-xs max-h-[400px] overflow-y-auto pr-1">
          {["name","description","org_type","country","city","telephone","email","website","address","category"].map(f => (
            <div key={f} className="space-y-0.5">
              <label className="text-[10px] text-app-secondary uppercase">{f === "org_type" ? "Type" : f}</label>
              {f === "description" ? (
                <textarea value={editForm[f] || ""} onChange={e => setEditForm({...editForm, [f]: e.target.value})}
                  className="w-full bg-surface-item border border-input rounded px-2 py-1 text-xs text-app focus:outline-none focus:border-violet-500" rows={3} />
              ) : (
                <input value={editForm[f] || ""} onChange={e => setEditForm({...editForm, [f]: e.target.value})}
                  className="w-full bg-surface-item border border-input rounded px-2 py-1 text-xs text-app focus:outline-none focus:border-violet-500" />
              )}
            </div>
          ))}
          <div className="space-y-0.5">
            <label className="text-[10px] text-app-secondary uppercase">Services (comma separated)</label>
            <input value={editForm.services || ""} onChange={e => setEditForm({...editForm, services: e.target.value})}
              className="w-full bg-surface-item border border-input rounded px-2 py-1 text-xs text-app focus:outline-none focus:border-violet-500" />
          </div>
          <div className="space-y-0.5">
            <label className="text-[10px] text-app-secondary uppercase">Wishes / Needs (comma separated)</label>
            <input value={editForm.wishes || ""} onChange={e => setEditForm({...editForm, wishes: e.target.value})}
              className="w-full bg-surface-item border border-input rounded px-2 py-1 text-xs text-app focus:outline-none focus:border-violet-500" />
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={saveOrgProfile} disabled={saving}
            className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-xs py-2 rounded-lg font-medium">{saving ? "Saving..." : "Save"}</button>
          <button onClick={() => setEditing(false)} className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white text-xs py-2 rounded-lg">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${logo.bg} flex items-center justify-center text-white font-bold text-xl shadow-lg shrink-0`}>{logo.letter}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-base font-bold">{org.name}</h1>
            {org.verified && <Verified className="w-4 h-4 text-emerald-400 shrink-0" />}
          </div>
          {org.subdomain && <p className="text-xs text-app-secondary">{org.subdomain}.allbaise.com</p>}
        </div>
        <button onClick={onBack} className="text-app-secondary hover:text-app p-1">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs mb-2">
        <div className="space-y-1">
          <div><span className="text-[9px] text-app-secondary uppercase">Type</span><p><Badge className="bg-violet-600/20 text-violet-300 text-[10px]">{org.org_type}</Badge></p></div>
          <div><span className="text-[9px] text-app-secondary uppercase">Country</span><p className="text-app">{org.country || "N/A"}</p></div>
          <div><span className="text-[9px] text-app-secondary uppercase">Location</span><p className="text-app">{org.city || org.country || "N/A"}</p></div>
          {org.telephone && <div><span className="text-[9px] text-app-secondary uppercase">Phone</span><p className="text-app">{org.telephone}</p></div>}
          {org.email && <div><span className="text-[9px] text-app-secondary uppercase">Email</span><p className="text-app">{org.email}</p></div>}
          {org.website && <div><span className="text-[9px] text-app-secondary uppercase">Website</span><p className="text-app text-blue-400">{org.website}</p></div>}
          {org.address && <div><span className="text-[9px] text-app-secondary uppercase">Address</span><p className="text-app">{org.address}</p></div>}
        </div>
        <div className="space-y-1">
          <div><span className="text-[9px] text-app-secondary uppercase">Category</span><p className="text-app">{org.category || "N/A"}</p></div>
          <div><span className="text-[9px] text-app-secondary uppercase">Followers</span><p className="text-app">{org.total_followers || 0}</p></div>
          <div>
            <h4 className="text-[9px] uppercase tracking-wider text-app-secondary mb-0.5">Services & Products</h4>
            <div className="flex flex-wrap gap-1">{org.services && Array.isArray(org.services) && org.services.length > 0 ? org.services.map((s, i) => (<Badge key={i} className="bg-amber-600/20 text-amber-400 text-[8px] px-1.5 py-0.5">{s}</Badge>)) : (<span className="text-[10px] text-app-secondary">None</span>)}</div>
          </div>
          <div>
            <h4 className="text-[9px] uppercase tracking-wider text-app-secondary mb-0.5">Wishes / Needs</h4>
            <div className="flex flex-wrap gap-1">{org.wishes && Array.isArray(org.wishes) && org.wishes.length > 0 ? org.wishes.map((w, i) => (<Badge key={i} className="bg-pink-600/20 text-pink-400 text-[8px] px-1.5 py-0.5">{w}</Badge>)) : (<span className="text-[10px] text-app-secondary">None</span>)}</div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 pt-3 border-t border-white/10">
        {isOwner ? (
          <>
            <button onClick={() => setEditing(true)}
              className="flex-1 bg-violet-600 hover:bg-violet-500 text-white text-[10px] font-medium py-2 rounded-lg flex items-center justify-center gap-1.5">
              <Edit3 className="w-3 h-3" /> Edit Org Profile
            </button>
            <Link to="/corporate-hub/settings"
              className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white text-[10px] font-medium py-2 rounded-lg flex items-center justify-center gap-1.5 text-center">
              Settings
            </Link>
          </>
        ) : (
          <>
            <button onClick={() => window.location.href="/comms-centre/mail/inbox"} className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-medium py-2 rounded-lg flex items-center justify-center gap-1.5">
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 4l-10 8L2 4"/></svg> Request Contact
            </button>
            <button onClick={() => window.location.href="/comms-centre/chat"} className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-medium py-2 rounded-lg flex items-center justify-center gap-1.5">
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg> Share Contact
            </button>
            <button onClick={() => window.location.href="/comms-centre/chat"} className="flex-1 bg-amber-600 hover:bg-amber-500 text-white text-[10px] font-medium py-2 rounded-lg flex items-center justify-center gap-1.5">
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg> Add to Group
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Corporate Sidebar ──
function CorporateSidebar({ onNavigate }) {
  const [orgs, setOrgs] = useState([]); const [loading, setLoading] = useState(true); const [search, setSearch] = useState("");
  const [csCountry, setCsCountry] = useState("all"); const [csRegion, setCsRegion] = useState("all");
  const [csOrgType, setCsOrgType] = useState("all"); const [csCategory, setCsCategory] = useState("all"); const [csRadius, setCsRadius] = useState("");
  useEffect(() => { setLoading(true); axios.get(`${API}/v2/orgs`, { headers: getHeaders() }).then(res => setOrgs(res.data.items || [])).catch(() => setOrgs([])).finally(() => setLoading(false)); }, []);
  const filtered = orgs.filter(o => { if (search && !(o.name || "").toLowerCase().includes(search.toLowerCase())) return false; if (csCategory !== "all" && o.category !== csCategory) return false; return true; });
  return (
    <div>
      <div className="flex items-center justify-between mb-2"><h3 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-violet-400" /> Corporate Hub</h3><button onClick={() => onNavigate("/corporate-hub")} className="text-xs text-violet-400 hover:text-violet-300 font-medium">View All</button></div>
      <GeoFilterBar country={csCountry} onCountryChange={setCsCountry} region={csRegion} onRegionChange={setCsRegion} radius={csRadius} onRadiusChange={setCsRadius} />
      <div className="flex gap-1 mb-1 overflow-x-auto scrollbar-dark">
        <button onClick={() => setCsOrgType("all")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csOrgType === "all" ? "bg-violet-600/30 text-violet-300 font-semibold" : "bg-surface-item text-app-secondary"}`}>All</button>
        {["government","ngo","business"].map(t => (<button key={t} onClick={() => setCsOrgType(t)} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csOrgType === t ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>{t}</button>))}
      </div>
      <div className="flex gap-1 mb-1 overflow-x-auto scrollbar-dark">
        <button onClick={() => setCsCategory("all")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "all" ? "bg-violet-600/30 text-violet-300 font-semibold" : "bg-surface-item text-app-secondary"}`}>All</button>
        {["National","Provincial","Municipal","State-Owned Entity","Regulatory","Human Rights","Education","Health","Environment","Community","Relief","Technology","Finance","Manufacturing","Retail","Healthcare","Energy","Agriculture","Logistics","Tourism"].map(c => (
          <button key={c} onClick={() => setCsCategory(c)} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === c ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>{c}</button>
        ))}
      </div>
      <div className="relative mb-2"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-secondary" /><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="w-full bg-surface-item border border-input rounded-lg pl-8 pr-3 py-1.5 text-xs text-app placeholder:text-app-secondary focus:outline-none focus:border-violet-500" /></div>
      <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
        {loading ? (<Loader2 className="w-4 h-4 animate-spin text-app-secondary mx-auto py-4" />) : filtered.length === 0 ? (<p className="text-xs text-app-secondary text-center py-4">No organisations found</p>) : (
          filtered.slice(0, 6).map(o => (
            <div key={o.id || o._id} onClick={() => onNavigate(`/corporate-hub/${o.id || o.subdomain}`)} className="bg-surface-item rounded-lg p-2 cursor-pointer hover:bg-surface-hover transition-colors">
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold shrink-0">{dummyLogo(o.name).letter}</div>
                <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate text-app">{o.name}</p><p className="text-[10px] text-app-secondary">{o.country || ""}</p></div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── People Sidebar ──
function PeopleSidebar({ onNavigate }) {
  const [people, setPeople] = useState([]); const [loading, setLoading] = useState(true); const [search, setSearch] = useState(""); const [psCountry, setPsCountry] = useState("all"); const [psRegion, setPsRegion] = useState("all");
  useEffect(() => { setLoading(true); axios.get(`${API}/v2/people`, { headers: getHeaders() }).then(res => setPeople(res.data.items || [])).catch(() => setPeople([])).finally(() => setLoading(false)); }, []);
  const filtered = people.filter(p => { if (search && !(p.name || "").toLowerCase().includes(search.toLowerCase())) return false; return true; });
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2"><h3 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-emerald-400" /> My People</h3><button onClick={() => onNavigate("/people-directory")} className="text-xs text-emerald-400 hover:text-emerald-300 font-medium">View All</button></div>
      <GeoFilterBar country={psCountry} onCountryChange={setPsCountry} region={psRegion} onRegionChange={setPsRegion} />
      <div className="relative mb-2"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-secondary" /><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="w-full bg-surface-item border border-input rounded-lg pl-8 pr-3 py-1.5 text-xs text-app placeholder:text-app-secondary focus:outline-none focus:border-emerald-500" /></div>
      <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
        {loading ? (<Loader2 className="w-4 h-4 animate-spin text-app-secondary mx-auto py-4" />) : filtered.length === 0 ? (<p className="text-xs text-app-secondary text-center py-4">No people found</p>) : (
          filtered.slice(0, 6).map(p => (
            <div key={p.user_id || p._id} onClick={() => onNavigate(`/people/${p.username || p.user_id}`)} className="bg-surface-item rounded-lg p-2 cursor-pointer hover:bg-surface-hover transition-colors">
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-xs font-bold shrink-0">{dummyAvatar(p.name, p.user_id).letter}</div>
                <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate text-app">{p.name}</p><p className="text-[10px] text-app-secondary">{p.position || p.country || ""}</p></div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── Mini Deals Sidebar ──
function MiniDealsSidebar({ onNavigate }) {
  const [deals, setDeals] = useState([]); const [loading, setLoading] = useState(true); const [dCountry, setDCountry] = useState("all"); const [dRegion, setDRegion] = useState("all"); const [dRadius, setDRadius] = useState(""); const [dCategory, setDCategory] = useState("all");
  useEffect(() => { setLoading(true); axios.get(`${API}/deals/list`, { headers: getHeaders() }).then(res => setDeals((res.data.items || []).filter(d => d.status === "active"))).catch(() => setDeals([])).finally(() => setLoading(false)); }, []);
  return (
    <div><div className="flex items-center justify-between mb-2"><h3 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5"><Tag className="w-3.5 h-3.5 text-amber-400" /> Deals</h3><button onClick={() => onNavigate("/deals")} className="text-xs text-amber-400 hover:text-amber-300 font-medium">View All</button></div>
    <GeoFilterBar country={dCountry} onCountryChange={setDCountry} region={dRegion} onRegionChange={setDRegion} radius={dRadius} onRadiusChange={setDRadius} />
      <div className="flex gap-1 mb-2 mt-2 overflow-x-auto scrollbar-dark">
        <button onClick={() => setDCategory("all")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${dCategory === "all" ? "bg-amber-600/30 text-amber-300" : "bg-surface-item text-app-secondary"}`}>All</button>
        {["Food & Drink","Health & Beauty","Travel & Hotels","Electronics","Fashion","Home & Garden","Automotive","Education","Services","Entertainment","Groceries","Other"].map(c => (<button key={c} onClick={() => setDCategory(c)} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${dCategory === c ? "bg-amber-600/30 text-amber-300" : "bg-surface-item text-app-secondary"}`}>{c}</button>))}
      </div>
    {loading ? (<Loader2 className="w-4 h-4 animate-spin text-app-secondary mx-auto py-4" />) : deals.length === 0 ? (<p className="text-xs text-app-secondary text-center py-4">No deals</p>) : (
      <div className="space-y-2 max-h-[220px] pr-1">{deals.slice(0, 5).map(deal => (
        <div key={deal.id} onClick={() => onNavigate("/deals")} className="bg-surface-item rounded-lg p-2 cursor-pointer hover:bg-surface-hover">
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-600 to-orange-600 flex items-center justify-center text-white text-xs font-bold shrink-0"><Tag className="w-4 h-4" /></div>
            <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate text-app">{deal.title}</p><span className="text-xs font-bold text-amber-400">R{deal.deal_price}</span></div>
          </div>
        </div>
      ))}</div>
    )}
    </div>
  );
}

// ── Mini Wishes Sidebar ──
function MiniWishesSidebar({ onNavigate }) {
  const [wishes, setWishes] = useState([]); const [loading, setLoading] = useState(true); const [wCountry, setWCountry] = useState("all"); const [wRegion, setWRegion] = useState("all"); const [wRadius, setWRadius] = useState(""); const [wCategory, setWCategory] = useState("all");
  useEffect(() => { setLoading(true); axios.get(`${API}/wishes/list`, { headers: getHeaders() }).then(res => setWishes(res.data.items || [])).catch(() => setWishes([])).finally(() => setLoading(false)); }, []);
  return (
    <div className="mt-3"><div className="flex items-center justify-between mb-2"><h3 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-pink-400" /> Wishes</h3><button onClick={() => onNavigate("/wish-centre")} className="text-xs text-pink-400 hover:text-pink-300 font-medium">View All</button></div>
    <GeoFilterBar country={wCountry} onCountryChange={setWCountry} region={wRegion} onRegionChange={setWRegion} radius={wRadius} onRadiusChange={setWRadius} />
      <div className="flex gap-1 mb-2 mt-2 overflow-x-auto scrollbar-dark">
        <button onClick={() => setWCategory("all")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wCategory === "all" ? "bg-pink-600/30 text-pink-300" : "bg-surface-item text-app-secondary"}`}>All</button>
        {["Services","Products","Jobs","Learning","Health","Travel","Technology","Home","Fashion","Food","Entertainment","Volunteering","Mentorship","Funding","Other"].map(c => (<button key={c} onClick={() => setWCategory(c)} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wCategory === c ? "bg-pink-600/30 text-pink-300" : "bg-surface-item text-app-secondary"}`}>{c}</button>))}
      </div>
    {loading ? (<Loader2 className="w-4 h-4 animate-spin text-app-secondary mx-auto py-4" />) : wishes.length === 0 ? (<p className="text-xs text-app-secondary text-center py-4">No wishes</p>) : (
      <div className="space-y-2 max-h-[220px] pr-1">{wishes.slice(0, 5).map(wish => (
        <div key={wish.id} onClick={() => onNavigate("/wish-centre")} className="bg-surface-item rounded-lg p-2 cursor-pointer hover:bg-surface-hover">
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white text-xs font-bold shrink-0"><Star className="w-4 h-4" /></div>
            <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate text-app">{wish.title}</p><p className="text-[10px] text-app-secondary">{wish.category}</p></div>
          </div>
        </div>
      ))}</div>
    )}
    </div>
  );
}

// ── ORG ACTIVITY FEED ──
function OrgActivityFeed({ orgId, isOwner }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postContent, setPostContent] = useState("");
  const [posting, setPosting] = useState(false);
  const [likedPosts, setLikedPosts] = useState({});
  const [reactions, setReactions] = useState({});
  const [shares, setShares] = useState({});
  const [showReactionPicker, setShowReactionPicker] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [commentsData, setCommentsData] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const auth = useAuth();

  const fetchPosts = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/org-activity/${orgId}`, { headers: getHeaders() });
      const items = res.data.items || [];
      setPosts(items);
      items.forEach(p => {
        fetchReactions(p.id);
        setShares(prev => ({ ...prev, [p.id]: p.shares || 0 }));
      });
    } catch { setPosts([]); }
    setLoading(false);
  }, [orgId]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handlePost = async () => {
    if (!postContent.trim()) return;
    setPosting(true);
    try {
      const res = await axios.post(`${API}/activity`, { content: postContent, org_id: orgId }, { headers: getHeaders() });
      setPosts(prev => [res.data, ...prev]);
      setPostContent("");
      toast.success("Posted!");
    } catch { toast.error("Failed to post"); }
    setPosting(false);
  };

  const handleLike = async (postId) => {
    try {
      const res = await axios.post(`${API}/activity/${postId}/like`, {}, { headers: getHeaders() });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes: p.likes + (res.data.status === "liked" ? 1 : -1) } : p));
      setLikedPosts(prev => ({ ...prev, [postId]: res.data.status === "liked" }));
    } catch {}
  };

  const handleReact = async (postId, reaction) => {
    try {
      const res = await axios.post(`${API}/activity/${postId}/react`, { reaction }, { headers: getHeaders() });
      setShowReactionPicker(prev => ({ ...prev, [postId]: false }));
      if (res.data.status === "reacted") {
        setReactions(prev => ({ ...prev, [postId]: { ...prev[postId], [reaction]: (prev[postId]?.[reaction] || 0) + 1 } }));
      }
    } catch {}
  };

  const handleShare = async (postId) => {
    try {
      const res = await axios.post(`${API}/activity/${postId}/share`, {}, { headers: getHeaders() });
      setShares(prev => ({ ...prev, [postId]: res.data.shares }));
      toast.success("Post shared!");
    } catch { toast.error("Failed to share"); }
  };

  const fetchComments = async (postId) => {
    try {
      const res = await axios.get(`${API}/activity/${postId}/comments`, { headers: getHeaders() });
      setCommentsData(prev => ({ ...prev, [postId]: res.data.items || [] }));
      setExpandedComments(prev => ({ ...prev, [postId]: true }));
    } catch {}
  };

  const handleComment = async (postId) => {
    const text = commentInputs[postId];
    if (!text?.trim()) return;
    try {
      const res = await axios.post(`${API}/activity/${postId}/comments`, { content: text }, { headers: getHeaders() });
      setCommentsData(prev => ({ ...prev, [postId]: [...(prev[postId] || []), res.data] }));
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments_count: (p.comments_count || 0) + 1 } : p));
      setCommentInputs(prev => ({ ...prev, [postId]: "" }));
    } catch { toast.error("Failed to comment"); }
  };

  const fetchReactions = async (postId) => {
    try {
      const res = await axios.get(`${API}/activity/${postId}/reactions`, { headers: getHeaders() });
      setReactions(prev => ({ ...prev, [postId]: res.data.reactions }));
    } catch {}
  };

  const totalReactions = (postId) => { const r = reactions[postId] || {}; return Object.values(r).reduce((a, b) => a + b, 0); };
  const REACTION_EMOJIS = { "like": "👍", "love": "❤️", "celebrate": "🎉", "support": "💪", "insightful": "💡" };

  const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = Math.floor((new Date() - d) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="space-y-4">
      {/* Post Composer (only for org owner) */}
      {isOwner && (
        <Card className="bg-app-card border-white/10 p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
              <Building2 className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <textarea value={postContent} onChange={e => setPostContent(e.target.value)}
                placeholder="Share something with your followers..."
                className="w-full bg-transparent border-0 text-sm text-app placeholder:text-app-secondary resize-none focus:outline-none min-h-[60px]" rows={2} />
              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                <div />
                <Button onClick={handlePost} disabled={posting || !postContent.trim()}
                  className="bg-violet-600 hover:bg-violet-500 text-white rounded-lg text-xs h-8 px-4">
                  {posting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />} Post
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Posts */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-app-secondary" /></div>
      ) : posts.length === 0 ? (
        <Card className="bg-app-card border-white/10 p-6 text-center">
          <p className="text-sm text-app-secondary">No posts yet.</p>
        </Card>
      ) : (
        posts.slice(0, 20).map(post => (
          <Card key={post.id} className="bg-app-card border-white/10 p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                <Building2 className="w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-medium">{post.author_name || "Organization"}</p>
                <p className="text-[10px] text-app-secondary">{formatTime(post.created_at)}</p>
              </div>
            </div>
            <p className="text-sm text-app mb-3 whitespace-pre-wrap">{post.content}</p>

            {/* Actions row */}
            <div className="flex items-center gap-3 text-xs text-app-secondary border-t border-white/10 pt-3 flex-wrap">
              {/* Reaction Picker */}
              <div className="relative">
                <button onClick={() => setShowReactionPicker(prev => ({ ...prev, [post.id]: !prev[post.id] }))}
                  className="flex items-center gap-1 hover:text-amber-400 transition">
                  <Heart className={`w-4 h-4 ${totalReactions(post.id) > 0 ? "fill-red-400 text-red-400" : ""}`} />
                  {totalReactions(post.id) || 0}
                </button>
                {showReactionPicker[post.id] && (
                  <div className="absolute bottom-full left-0 mb-1 flex gap-1 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 shadow-xl z-10">
                    {Object.entries(REACTION_EMOJIS).map(([key, emoji]) => (
                      <button key={key} onClick={() => handleReact(post.id, key)} className="hover:scale-125 transition-transform text-sm p-0.5" title={key}>{emoji}</button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => handleReact(post.id, "like")}
                className={`flex items-center gap-1 hover:text-red-400 transition ${likedPosts[post.id] ? "text-red-400" : ""}`}>
                <Heart className={`w-4 h-4 ${likedPosts[post.id] ? "fill-red-400" : ""}`} />{post.likes || 0}
              </button>
              <button onClick={() => handleShare(post.id)} className="flex items-center gap-1 hover:text-emerald-400 transition">
                <Share2 className="w-4 h-4" />{shares[post.id] || 0}
              </button>
              <button onClick={() => { expandedComments[post.id] ? setExpandedComments(prev => ({ ...prev, [post.id]: false })) : fetchComments(post.id); }}
                className="flex items-center gap-1 hover:text-blue-400 transition">
                <MessageCircle className="w-4 h-4" />{post.comments_count || 0}
              </button>
            </div>

            {/* Reactions display */}
            {totalReactions(post.id) > 0 && (
              <div className="flex items-center gap-1.5 pt-1 text-xs">
                {Object.entries(REACTION_EMOJIS).map(([key, emoji]) => {
                  const count = reactions[post.id]?.[key] || 0;
                  if (count === 0) return null;
                  return (<span key={key} className="flex items-center gap-0.5 text-app-secondary"><span>{emoji}</span><span className="text-[10px]">{count}</span></span>);
                })}
              </div>
            )}

            {/* Comments */}
            {expandedComments[post.id] && (
              <div className="mt-3 pt-3 border-t border-white/10 space-y-3">
                {commentsData[post.id]?.map(c => (
                  <div key={c.id} className="flex items-start gap-2">
                    <Avatar className="w-6 h-6 shrink-0"><AvatarFallback className="bg-zinc-700 text-[10px] text-white">{(c.author_name || "U")[0]}</AvatarFallback></Avatar>
                    <div className="flex-1 bg-surface-item rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2"><span className="text-xs font-medium">{c.author_name}</span><span className="text-[10px] text-app-secondary">{formatTime(c.created_at)}</span></div>
                      <p className="text-xs text-app mt-0.5">{c.content}</p>
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <input type="text" value={commentInputs[post.id] || ""} onChange={e => setCommentInputs(prev => ({ ...prev, [post.id]: e.target.value }))}
                    onKeyDown={e => e.key === "Enter" && handleComment(post.id)} placeholder="Write a comment..."
                    className="flex-1 bg-surface-item border border-input rounded-lg px-3 py-1.5 text-sm text-app placeholder:text-app-secondary focus:outline-none focus:border-violet-500" />
                  <button onClick={() => handleComment(post.id)} className="text-violet-400 hover:text-violet-300"><Send className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </Card>
        ))
      )}
    </div>
  );
}

// ── CONNECT WITH US — Replaces wishlist on org pages ──
function ConnectWithUs({ org, isOwner, onUpdate }) {
  const auth = useAuth();
  const [expandedSection, setExpandedSection] = useState(null);
  const [quotationItems, setQuotationItems] = useState([]);
  const [bookingItems, setBookingItems] = useState([]);
  const [contactMessage, setContactMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [showCreditPopup, setShowCreditPopup] = useState(false);
  const [editingServices, setEditingServices] = useState(false);
  const [servicesInput, setServicesInput] = useState("");
  const [savingServices, setSavingServices] = useState(false);
  const services = org?.services || [];

  const sendRequest = async (type, items, message) => {
    if (!auth?.user) { toast.error("Please log in first"); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/orgs/${org.id}/request`,
        { type, items: items || [], message: message || "" },
        { headers: getHeaders() }
      );
      if (res.data.ok === false && res.data.error === "insufficient_credits") {
        setShowCreditPopup(true);
        return;
      }
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} request sent!`);
      setQuotationItems([]);
      setBookingItems([]);
      setContactMessage("");
      setExpandedSection(null);
    } catch (err) {
      if (err.response?.data?.detail?.includes("credit")) setShowCreditPopup(true);
      else toast.error(err.response?.data?.detail || "Failed to send");
    }
    setSending(false);
  };

  const toggleItem = (arr, setter, item) => {
    setter(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
  };

  const startEditingServices = () => {
    setServicesInput(services.join(", "));
    setEditingServices(true);
  };

  const saveServices = async () => {
    setSavingServices(true);
    try {
      const updated = servicesInput.split(",").map(s => s.trim()).filter(Boolean);
      await axios.put(API + "/v2/orgs/" + (org.id || org.subdomain),
        { services: updated },
        { headers: getHeaders() }
      );
      toast.success("Services updated!");
      setEditingServices(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error("Failed to save services");
    }
    setSavingServices(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
          <Building2 className="w-3.5 h-3.5 text-cyan-400" /> Connect With Us
        </h3>
      </div>

      {showCreditPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShowCreditPopup(false)}>
          <div className="bg-app-card border border-amber-600/40 rounded-xl p-6 max-w-sm mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mx-auto mb-3 shadow-lg">
                <Star className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Credits Required</h3>
              <p className="text-sm text-app-secondary mb-4">You need 1 ALL to send this request.</p>
              <div className="bg-gradient-to-r from-amber-600/20 to-orange-600/20 border border-amber-600/30 rounded-lg p-3 mb-4">
                <p className="text-xs text-app-secondary">💎 100 ALLs = R10.00 = ~$0.50 USD</p>
                <p className="text-xs text-amber-400 mt-1">Buy credits to unlock all features</p>
              </div>
              <div className="flex gap-2">
                <Link to="/aipps" className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white px-4 py-2.5 rounded-lg text-sm font-semibold text-center transition-all shadow-lg">
                  💰 Buy Credits
                </Link>
                <button onClick={() => setShowCreditPopup(false)} className="px-4 py-2.5 bg-surface-item text-app-secondary hover:text-white rounded-lg text-sm transition-colors">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {/* 1. What we do for you */}
        <div>
          <button onClick={() => setExpandedSection(expandedSection === "services" ? null : "services")}
            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium bg-gradient-to-r from-cyan-600/20 to-blue-600/20 text-cyan-300 hover:from-cyan-600/30 hover:to-blue-600/30 transition-all">
            <Briefcase className="w-3.5 h-3.5" /><span className="flex-1 text-left">What we do for you</span>
            <span className="text-[10px] opacity-70">({services.length})</span>
            {expandedSection === "services" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {expandedSection === "services" && (
            <div className="mt-1 space-y-1 pl-3">
              {editingServices ? (
                <div className="space-y-2">
                  <textarea value={servicesInput} onChange={e => setServicesInput(e.target.value)}
                    placeholder="Enter services separated by commas"
                    rows={4}
                    className="w-full text-xs bg-surface-input border border-input rounded p-2 text-app focus:outline-none focus:border-cyan-500 resize-none placeholder:text-placeholder"
                  />
                  <div className="flex gap-2">
                    <button onClick={saveServices} disabled={savingServices}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-xs py-1.5 rounded font-medium">Save</button>
                    <button onClick={() => setEditingServices(false)} className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white text-xs py-1.5 rounded">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="max-h-[200px] overflow-y-auto space-y-1">
                  {services.length === 0 ? (<p className="text-[10px] text-app-secondary py-2 italic">No services listed</p>) : (
                    services.map((s, i) => (<div key={i} className="bg-surface-item rounded-lg p-2"><p className="text-[11px] font-medium text-app">{s}</p></div>))
                  )}
                </div>
              )}
              {isOwner && !editingServices && (
                <button onClick={startEditingServices}
                  className="flex items-center gap-1 text-[10px] text-cyan-400 hover:text-cyan-300 mt-1">
                  <Edit3 className="w-3 h-3" /> Edit Services
                </button>
              )}
            </div>
          )}
        </div>

        {/* 2. Request Quotation */}
        <div>
          <button onClick={() => setExpandedSection(expandedSection === "quotation" ? null : "quotation")}
            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium bg-gradient-to-r from-emerald-600/20 to-teal-600/20 text-emerald-300 hover:from-emerald-600/30 hover:to-teal-600/30 transition-all">
            <Target className="w-3.5 h-3.5" /><span className="flex-1 text-left">Request a Quotation</span>
            {expandedSection === "quotation" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {expandedSection === "quotation" && (
            <div className="mt-1 space-y-1 pl-3">
              {services.length === 0 ? (<p className="text-[10px] text-app-secondary py-1 italic">No services available</p>) : (
                services.map((s, i) => (
                  <label key={i} className="flex items-center gap-2 bg-surface-item rounded-lg p-2 cursor-pointer hover:bg-surface-hover">
                    <input type="checkbox" checked={quotationItems.includes(s)} onChange={() => toggleItem(quotationItems, setQuotationItems, s)}
                      className="w-3.5 h-3.5 rounded accent-emerald-500" />
                    <span className="text-[11px] text-app">{s}</span>
                  </label>
                ))
              )}
              {quotationItems.length > 0 && (
                <button onClick={() => sendRequest("quotation", quotationItems, "")} disabled={sending}
                  className="w-full mt-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50">
                  {sending ? "Sending..." : `Request Quotation (${quotationItems.length} items)`}
                </button>
              )}
            </div>
          )}
        </div>

        {/* 3. Booking */}
        <div>
          <button onClick={() => setExpandedSection(expandedSection === "booking" ? null : "booking")}
            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium bg-gradient-to-r from-violet-600/20 to-purple-600/20 text-violet-300 hover:from-violet-600/30 hover:to-purple-600/30 transition-all">
            <Calendar className="w-3.5 h-3.5" /><span className="flex-1 text-left">Booking</span>
            {expandedSection === "booking" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {expandedSection === "booking" && (
            <div className="mt-1 space-y-1 pl-3">
              {services.length === 0 ? (<p className="text-[10px] text-app-secondary py-1 italic">No services for booking</p>) : (
                services.map((s, i) => (
                  <label key={i} className="flex items-center gap-2 bg-surface-item rounded-lg p-2 cursor-pointer hover:bg-surface-hover">
                    <input type="checkbox" checked={bookingItems.includes(s)} onChange={() => toggleItem(bookingItems, setBookingItems, s)}
                      className="w-3.5 h-3.5 rounded accent-violet-500" />
                    <span className="text-[11px] text-app">{s}</span>
                  </label>
                ))
              )}
              {bookingItems.length > 0 && (
                <button onClick={() => sendRequest("booking", bookingItems, "")} disabled={sending}
                  className="w-full mt-2 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-400 hover:to-purple-400 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50">
                  {sending ? "Sending..." : `Request Booking (${bookingItems.length} items)`}
                </button>
              )}
            </div>
          )}
        </div>

        {/* 4. Contact Us */}
        <div>
          <button onClick={() => setExpandedSection(expandedSection === "contact" ? null : "contact")}
            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium bg-gradient-to-r from-rose-600/20 to-pink-600/20 text-rose-300 hover:from-rose-600/30 hover:to-pink-600/30 transition-all">
            <Mail className="w-3.5 h-3.5" /><span className="flex-1 text-left">Contact Us</span>
            {expandedSection === "contact" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {expandedSection === "contact" && (
            <div className="mt-1 space-y-2 pl-3">
              <textarea value={contactMessage} onChange={e => setContactMessage(e.target.value)}
                placeholder={"Write your message to " + (org?.name || "the organization") + "..."}
                rows={3}
                className="w-full text-sm bg-surface-input border border-app rounded-lg p-2 text-app focus:outline-none focus:border-rose-500 resize-none placeholder:text-placeholder"
              />
              <button onClick={() => sendRequest("contact", [], contactMessage)} disabled={sending || !contactMessage.trim()}
                className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-400 hover:to-pink-400 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-1.5">
                <Send className="w-3.5 h-3.5" /> {sending ? "Sending..." : "Send Message"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── MAIN EXPORT ──
export default function OrgProfilePage() {
  const navigate = useNavigate();
  const { orgId } = useParams();
  const auth = useAuth();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMobileSidebars, setShowMobileSidebars] = useState(window.innerWidth >= 1024);
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    if (!orgId) { setLoading(false); setError("No organization ID"); return; }
    setLoading(true);
    setError(null);
    setOrg(null);
    setIsOwner(false);
    axios.get(`${API}/v2/orgs/${orgId}`)
      .then(res => {
        if (res.data?.error) { setError(res.data.error); setOrg(null); }
        else { setOrg(res.data); }
      })
      .catch(err => { setError(err.message || "Failed"); setOrg(null); })
      .finally(() => setLoading(false));
  }, [orgId]);

  // Determine ownership — check created_by field AND my-org endpoint as fallback
  useEffect(() => {
    if (!auth.user || !org) return;
    if (auth.user.role === "admin" || org.created_by === auth.user.user_id) {
      setIsOwner(true);
      return;
    }
    axios.get(API + "/auth/my-org", { headers: getHeaders() })
      .then(res => setIsOwner(!!res.data.org))
      .catch(() => setIsOwner(false));
  }, [auth.user, org]);

  if (loading) {
    return (<div className="min-h-screen bg-app text-app"><TopNav /><div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-6 h-6 animate-spin text-app-secondary" /></div></div>);
  }

  if (error || !org) {
    return (
      <div className="min-h-screen bg-app text-app">
        <TopNav />
        <div className="max-w-4xl mx-auto px-6 py-8">
          <Card className="bg-app-card border-white/10 p-8 text-center">
            <Building2 className="w-12 h-12 text-app-secondary mx-auto mb-3" />
            <p className="text-app-secondary mb-4">{error || "Could not load organization"}</p>
            <Button onClick={() => navigate("/corporate-hub")} variant="outline">Back to Corporate Hub</Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app text-app">
      <TopNav />
      <div className="max-w-7xl mx-auto px-3 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

          {/* TOP ROW: Org Business Card + Wishlist */}
          <div className="lg:col-span-9">
            <Card className="bg-app-card border-white/10 p-3">
              <OrgBusinessCard org={org} onBack={() => navigate("/corporate-hub")} isOwner={isOwner} />
            </Card>
          </div>
          <div className="lg:col-span-3">
            <Card className="bg-app-card border-white/10 p-3">
              <ConnectWithUs org={org} isOwner={isOwner} onUpdate={() => window.location.reload()} />
            </Card>
          </div>

          {/* LEFT COLUMN */}
          <div className="lg:hidden col-span-full flex gap-2 mb-2">
            <button onClick={() => setShowMobileSidebars(!showMobileSidebars)}
              className="flex-1 bg-surface-item hover:bg-surface-hover text-zinc-300 text-xs py-2 rounded-lg font-medium transition-colors">
              {showMobileSidebars ? "Hide Sidebars ▲" : "Show Sidebars ▼"}
            </button>
          </div>
          {showMobileSidebars && (
          <div className="lg:col-span-3 space-y-3">
            <Card className="bg-app-card border-white/10 p-3"><CorporateSidebar onNavigate={navigate} /></Card>
            <Card className="bg-app-card border-white/10 p-3"><PeopleSidebar onNavigate={navigate} /></Card>
          </div>
          )}

          {/* CENTER: Activity Feed */}
          <div className="lg:col-span-6 space-y-4">
            <OrgActivityFeed orgId={org.id || orgId} isOwner={isOwner} />
          </div>

          {/* RIGHT COLUMN */}
          {showMobileSidebars && (
          <div className="lg:col-span-3 space-y-3">
            <Card className="bg-app-card border-white/10 p-3"><MiniDealsSidebar onNavigate={navigate} /></Card>
            <Card className="bg-app-card border-white/10 p-3"><MiniWishesSidebar onNavigate={navigate} /></Card>
          </div>
          )}

        </div>
      </div>
    </div>
  );
}
